// @djsem/shared — show-direction contracts: the editable objects a DJ shapes in prep.
// These are MEANING-level objects (rule 7): no renderer, protocol, or hardware terms.
// Generated objects carry source+confidence; manual edits become overrides on clones
// (rule 6) — nothing here mutates an analysis package.
import {
  vArray, vBoolean, vId, vIsoDate, vNumber, vObject, vOptionalNull, vRefine, vString, vUnit,
  type Validator,
} from "./validation";

// ---- approval (shared by set / show pack) -----------------------------------

/** Review/approval lifecycle status shared by sets and show packs. */
export type DJSEMApprovalStatus = "draft" | "in_review" | "approved" | "rejected" | "superseded";
/** Approval lifecycle state for a set or show pack. */
export interface DJSEMApprovalState {
  status: DJSEMApprovalStatus;
  approvedAtIso?: string;
  approvedBy?: string;
  snapshotHash?: string;
  notes?: string;
}
/** Runtime validator for {@link DJSEMApprovalState}. */
export const vApprovalState: Validator<DJSEMApprovalState> = vObject(
  { status: vString({ enum: ["draft", "in_review", "approved", "rejected", "superseded"] }) },
  { approvedAtIso: vIsoDate(), approvedBy: vString({ min: 1 }), snapshotHash: vString({ min: 8 }), notes: vString({}) },
) as Validator<DJSEMApprovalState>;

// ---- provenance -------------------------------------------------------------

/** How a value in a DJSEM contract was produced: derived from analysis, entered manually, imported, or estimated as a fallback. */
export type DJSEMContractSource = "analysis" | "manual" | "imported" | "estimated";
const vSource = vString({ enum: ["analysis", "manual", "imported", "estimated"] });

// ---- track chapters ----------------------------------------------------------

/** The musical/structural role of a track chapter. */
export type DJSEMChapterType =
  | "opening" | "development" | "build" | "peak" | "recovery" | "closing"
  | "intro" | "verse" | "chorus" | "breakdown" | "drop" | "outro" | "transition" | "custom";
/** All valid {@link DJSEMChapterType} values. */
export const DJSEM_CHAPTER_TYPES: DJSEMChapterType[] = ["opening","development","build","peak","recovery","closing","intro","verse","chorus","breakdown","drop","outro","transition","custom"];
/** Review lifecycle status for a track chapter. */
export type DJSEMChapterReviewStatus = "not_reviewed" | "needs_review" | "reviewed" | "approved" | "rejected";
/** A structural section of a track (e.g. build, peak, breakdown), with energy/semantic/scene links. */
export interface DJSEMChapter {
  id: string;
  title: string;
  description?: string;
  chapterType: DJSEMChapterType;
  startBar?: number;
  endBar?: number;
  startTimeMs?: number;
  endTimeMs?: number;
  energyStart: number;
  energyEnd: number;
  semanticIntentIds: string[];
  visualSceneIds: string[];
  lightingSceneIds: string[];
  transitionInId?: string;
  transitionOutId?: string;
  confidence: number;
  source: DJSEMContractSource;
  reviewStatus?: DJSEMChapterReviewStatus;
}
/** Runtime validator for {@link DJSEMChapter}, enforcing startBar<=endBar and startTimeMs<=endTimeMs. */
export const vChapter: Validator<DJSEMChapter> = vRefine(
  vObject(
    {
      id: vId(), title: vString({ min: 1 }),
      chapterType: vString({ enum: ["opening", "development", "build", "peak", "recovery", "closing", "intro", "verse", "chorus", "breakdown", "drop", "outro", "transition", "custom"] }),
      energyStart: vUnit(), energyEnd: vUnit(),
      semanticIntentIds: vArray(vId()), visualSceneIds: vArray(vId()), lightingSceneIds: vArray(vId()),
      confidence: vUnit(), source: vSource,
    },
    {
      description: vString({}), startBar: vNumber({ min: 0 }), endBar: vNumber({ min: 0 }),
      startTimeMs: vNumber({ min: 0 }), endTimeMs: vNumber({ min: 0 }),
      transitionInId: vId(), transitionOutId: vId(),
      reviewStatus: vString({ enum: ["not_reviewed", "needs_review", "reviewed", "approved", "rejected"] }),
    },
  ) as Validator<DJSEMChapter>,
  (c) => {
    if (c.startBar !== undefined && c.endBar !== undefined && c.startBar > c.endBar) return "startBar must be <= endBar";
    if (c.startTimeMs !== undefined && c.endTimeMs !== undefined && c.startTimeMs > c.endTimeMs) return "startTimeMs must be <= endTimeMs";
    return null;
  },
);

// ---- phrase chapters (bar-accurate musical phrasing under a track chapter) ----

/** Bar-accurate musical phrasing nested under a track chapter (e.g. a rekordbox phrase family). */
export interface DJSEMPhraseChapter {
  id: string;
  parentChapterId?: string;
  phraseLabel: string; // taxonomy phrase label (e.g. rekordbox phrase family) — string to stay taxonomy-version-neutral
  startBar: number;
  endBar: number;
  energy: number;
  confidence: number;
  source: DJSEMContractSource;
}
/** Runtime validator for {@link DJSEMPhraseChapter}, enforcing startBar<=endBar. */
export const vPhraseChapter: Validator<DJSEMPhraseChapter> = vRefine(
  vObject(
    { id: vId(), phraseLabel: vString({ min: 1 }), startBar: vNumber({ min: 0 }), endBar: vNumber({ min: 0 }), energy: vUnit(), confidence: vUnit(), source: vSource },
    { parentChapterId: vId() },
  ) as Validator<DJSEMPhraseChapter>,
  (p) => (p.startBar > p.endBar ? "startBar must be <= endBar" : null),
);

// ---- cue points ---------------------------------------------------------------

/** What a cue point marks in a track's structure. */
export type DJSEMCueIntent =
  | "intro_start" | "mix_in" | "vocal_entry" | "build_start" | "drop"
  | "breakdown" | "mix_out" | "impact" | "manual";
/** Where a cue point originated from. */
export type DJSEMCueSource = "rekordbox" | "pioneer" | "analysis" | "manual" | "estimated" | "imported";
/** Review lifecycle status for a cue point. */
export type DJSEMCueReviewStatus = "not_reviewed" | "needs_review" | "reviewed" | "approved" | "rejected";
/** A single cue point anchored to a bar or a time offset within a track. */
export interface DJSEMCuePoint {
  id: string;
  label: string;
  intent: DJSEMCueIntent;
  bar?: number;
  beat?: number;
  timeMs?: number;
  confidence: number;
  source: DJSEMCueSource;
  approved: boolean;
  reviewStatus?: DJSEMCueReviewStatus;
  warnings?: string[];
  sourceTrace?: Record<string, unknown>;
}
/** Runtime validator for {@link DJSEMCuePoint}, enforcing the cue anchors to a bar or a timeMs. */
export const vCuePoint: Validator<DJSEMCuePoint> = vRefine(
  vObject(
    {
      id: vId(), label: vString({ min: 1 }),
      intent: vString({ enum: ["intro_start", "mix_in", "vocal_entry", "build_start", "drop", "breakdown", "mix_out", "impact", "manual"] }),
      confidence: vUnit(),
      source: vString({ enum: ["rekordbox", "pioneer", "analysis", "manual", "estimated", "imported"] }),
      approved: vBoolean,
    },
    { bar: vNumber({ min: 0 }), beat: vNumber({ min: 0 }), timeMs: vNumber({ min: 0 }),
      reviewStatus: vString({ enum: ["not_reviewed", "needs_review", "reviewed", "approved", "rejected"] }),
      warnings: vArray(vString({})),
      // Optional audit-provenance record (e.g. {fromChapterId, fromCueId}). The suggest/import
      // helpers populate this; must be validator-legal so a suggested cue can be approved.
      sourceTrace: (v) => (v === undefined || (typeof v === "object" && v !== null && !Array.isArray(v)))
        ? { valid: true, issues: [] } : { valid: false, issues: [{ path: "$", message: "sourceTrace must be an object" }] } },
  ) as Validator<DJSEMCuePoint>,
  (c) => (c.bar === undefined && c.timeMs === undefined ? "cue must anchor to a bar or a timeMs" : null),
);

// ---- set graph ----------------------------------------------------------------

/** A track's attributes as a node in the {@link DJSEMTrackRelationshipGraph}. */
export interface DJSEMTrackGraphNode {
  preparationTrackId: string;
  title: string;
  artist?: string;
  bpm?: number;
  key?: string;
  energy?: number;
  moodTags: string[];
  semanticDomains: string[];
  semanticCategories: string[];
  approvedForLive: boolean;
  warnings: string[];
}
/** Runtime validator for {@link DJSEMTrackGraphNode}. */
export const vTrackGraphNode: Validator<DJSEMTrackGraphNode> = vObject(
  { preparationTrackId: vId(), title: vString({ min: 1 }), moodTags: vArray(vString({ min: 1 })), semanticDomains: vArray(vString({ min: 1 })), approvedForLive: vBoolean },
  { artist: vString({}), bpm: vNumber({ min: 20, max: 400 }), key: vString({ min: 1 }), energy: vUnit() },
) as Validator<DJSEMTrackGraphNode>;

/** Machine-readable reason a track-graph edge scored the way it did. */
export type DJSEMGraphEdgeReason =
  | "bpm_close" | "key_compatible" | "energy_flow" | "semantic_continuity" | "chapter_fit" | "manual_link"
  | "mood_continuity" | "cue_compatible" | "warning";
/** Prompt 10 canonical name for {@link DJSEMGraphEdgeReason} — same union. */
export type DJSEMTrackGraphEdgeReason = DJSEMGraphEdgeReason;
/** All valid {@link DJSEMGraphEdgeReason} values. */
export const DJSEM_TRACK_GRAPH_EDGE_REASONS: DJSEMGraphEdgeReason[] = [
  "bpm_close", "key_compatible", "energy_flow", "semantic_continuity", "chapter_fit",
  "manual_link", "mood_continuity", "cue_compatible", "warning",
];
/** An explainable, scored edge between two tracks in the track relationship graph. */
export interface DJSEMTrackGraphEdge {
  id: string;
  fromPreparationTrackId: string;
  toPreparationTrackId: string;
  compatibilityScore: number;
  transitionRisk: "low" | "medium" | "high" | "unknown";
  reasons: DJSEMGraphEdgeReason[];
  explanation: string;
  warnings: string[];
  direction: "directed" | "undirected";
}
/** Runtime validator for {@link DJSEMTrackGraphEdge}. */
export const vTrackGraphEdge: Validator<DJSEMTrackGraphEdge> = vObject(
  {
    id: vId(), fromPreparationTrackId: vId(), toPreparationTrackId: vId(), compatibilityScore: vUnit(),
    transitionRisk: vString({ enum: ["low", "medium", "high", "unknown"] }),
    reasons: vArray(vString({ enum: DJSEM_TRACK_GRAPH_EDGE_REASONS }), { min: 1 }),
    explanation: vString({}), warnings: vArray(vString({})),
    direction: vString({ enum: ["directed", "undirected"] }),
  },
) as Validator<DJSEMTrackGraphEdge>;

// ---- set builder ---------------------------------------------------------------

/** The overall energy arc shape planned for a set. */
export type DJSEMSetArc = "rise_peak_resolve" | "slow_build" | "wave" | "constant_pressure" | "festival_peak" | "custom";
/** A single track's placement within a set plan. */
export interface DJSEMSetTrackEntry {
  preparationTrackId: string;
  order: number;
  plannedStartMs?: number;
  plannedDurationMs?: number;
  chapterIds: string[];
  notes?: string;
}
/** A complete set plan: ordered tracks, chapters, energy arc, and approval state. */
export interface DJSEMSetPlan {
  id: string;
  title: string;
  description?: string;
  tracks: DJSEMSetTrackEntry[];
  chapters: DJSEMChapter[];
  setArc: DJSEMSetArc;
  targetDurationMinutes?: number;
  approval: DJSEMApprovalState;
}
const vSetTrackEntry: Validator<DJSEMSetTrackEntry> = vObject(
  { preparationTrackId: vId(), order: vNumber({ min: 0, int: true }), chapterIds: vArray(vId()) },
  { plannedStartMs: vNumber({ min: 0 }), plannedDurationMs: vNumber({ min: 0 }), notes: vString({}) },
) as Validator<DJSEMSetTrackEntry>;
/** Runtime validator for {@link DJSEMSetPlan}. */
export const vSetPlan: Validator<DJSEMSetPlan> = vObject(
  {
    id: vId(), title: vString({ min: 1 }), tracks: vArray(vSetTrackEntry), chapters: vArray(vChapter),
    setArc: vString({ enum: ["rise_peak_resolve", "slow_build", "wave", "constant_pressure", "festival_peak", "custom"] }),
    approval: vApprovalState,
  },
  { description: vString({}), targetDurationMinutes: vNumber({ min: 1 }) },
) as Validator<DJSEMSetPlan>;

// ---- transition plans -----------------------------------------------------------

/** The technique planned for a transition between two tracks. */
export type DJSEMTransitionType = "blend" | "cut" | "echo_out" | "loop_build" | "breakdown_handoff" | "drop_switch" | "manual";
/** A planned or suggested transition between two tracks, with cue/scene links and confidence. */
export interface DJSEMTransitionPlan {
  id: string;
  fromTrackId: string;
  toTrackId: string;
  transitionType: DJSEMTransitionType;
  mixOutCueId?: string;
  mixInCueId?: string;
  visualSceneId?: string;
  lightingSceneId?: string;
  notes?: string;
  confidence: number;
  source: "analysis" | "manual" | "estimated";
  /** Explanation of why this plan was suggested (analysis) or chosen (manual). */
  reasons?: string[];
  /** Non-blocking issues (e.g. missing mix_out cue, low confidence). */
  warnings?: string[];
}
/** Runtime validator for {@link DJSEMTransitionPlan}. */
export const vTransitionPlan: Validator<DJSEMTransitionPlan> = vObject(
  {
    id: vId(), fromTrackId: vId(), toTrackId: vId(),
    transitionType: vString({ enum: ["blend", "cut", "echo_out", "loop_build", "breakdown_handoff", "drop_switch", "manual"] }),
    confidence: vUnit(), source: vString({ enum: ["analysis", "manual", "estimated"] }),
  },
  { mixOutCueId: vId(), mixInCueId: vId(), visualSceneId: vId(), lightingSceneId: vId(), notes: vString({}) },
) as Validator<DJSEMTransitionPlan>;

/** Null-tolerant helper for optional-with-null JSON payloads (alias of {@link vOptionalNull}). */
export const vNullable = vOptionalNull;
